import Cropper, { Area, Point } from 'react-easy-crop';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Slider from '@mui/material/Slider';
import { LoadingButton } from '@mui/lab';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useTranslate } from 'src/locales';

import { getCroppedSquareFile, CroppedImageOptions } from 'src/components/image-cropper/cropper-utils';

// ----------------------------------------------------------------------

const ZoomMarks = [
  { value: 1, label: '1x' },
  { value: 2, label: '2x' },
  { value: 3, label: '3x' },
];

const RotationMarks = [
  { value: 0, label: '0°' },
  { value: 90, label: '90°' },
  { value: 180, label: '180°' },
  { value: 270, label: '270°' },
];

interface Props {
  open: boolean;
  /** Local object/data URL of the image to crop (never a remote URL — crop happens client-side). */
  imageSrc: string;
  title?: string;
  /** Encoding + size-cap options passed straight to getCroppedSquareFile. */
  cropOptions?: CroppedImageOptions;
  /** True while the parent uploads the cropped file. */
  saving?: boolean;
  onClose: () => void;
  /** Receives the cropped, size-capped square File; the parent uploads + persists it. */
  onCropped: (file: File) => void | Promise<void>;
}

/**
 * Square image-crop dialog for the avatar/logo camera affordance (JT1-1539). A focused sibling of the
 * general ImageCropper: locked to a 1:1 square, with zoom + rotation, producing a ~1MB JPG/PNG File.
 * Kept separate so the avatar flow can crop a local file with no upload round-trip.
 */
export default function SquareCropDialog({
  open,
  imageSrc,
  title,
  cropOptions,
  saving = false,
  onClose,
  onCropped,
}: Props) {
  const { t } = useTranslate();

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return;
    const file = await getCroppedSquareFile(imageSrc, croppedAreaPixels, rotation, cropOptions);
    if (!file) return;
    await onCropped(file);
  }, [croppedAreaPixels, imageSrc, rotation, cropOptions, onCropped]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={saving ? undefined : onClose}>
      <DialogTitle>{title ?? t('components.image_cropper.title')}</DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ minHeight: 360, py: 4, px: 2, overflow: 'hidden', position: 'relative' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            minZoom={1}
            maxZoom={3}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </Box>

        <Stack direction="row" columnGap={2} sx={{ pt: 2, px: 2 }}>
          <Box sx={{ flexGrow: 1, p: 1 }}>
            <Typography variant="body2">{t('components.image_cropper.zoom')}</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              marks={ZoomMarks}
              valueLabelDisplay="auto"
              aria-label={t('components.image_cropper.zoom')}
              onChange={(_e, value) => setZoom(value as number)}
            />
          </Box>

          <Box sx={{ flexGrow: 1, p: 1 }}>
            <Typography variant="body2">{t('components.image_cropper.rotation')}</Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              marks={RotationMarks}
              valueLabelDisplay="auto"
              aria-label={t('components.image_cropper.rotation')}
              onChange={(_e, value) => setRotation(value as number)}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" disabled={saving} onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <LoadingButton variant="contained" color="primary" loading={saving} onClick={handleSave}>
          {t('common.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
